/**
 * Forge Suite skin registry (VividLex-style).
 *
 * To add a skin:
 * 1. Extend ThemeId in themes/types.ts
 * 2. Append a ThemeDefinition below
 * 3. Hot-reload — it appears in Settings → Skins
 */

import type { ThemeDefinition, ThemeId, ThemeTokens } from "./types";

const forgeFonts = {
  "--pf-font-display": '"Orbitron", "Segoe UI", sans-serif',
  "--pf-font-body": '"Rajdhani", "Segoe UI", sans-serif',
};

const cleanFonts = {
  "--pf-font-display": '"Segoe UI", "Inter", system-ui, sans-serif',
  "--pf-font-body": '"Segoe UI", "Inter", system-ui, sans-serif',
};

const serifFonts = {
  "--pf-font-display": 'Georgia, "Palatino Linotype", serif',
  "--pf-font-body": 'Georgia, "Palatino Linotype", "Times New Roman", serif',
};

const monoFonts = {
  "--pf-font-display": 'ui-monospace, "Cascadia Code", Consolas, monospace',
  "--pf-font-body": 'ui-monospace, "Cascadia Code", Consolas, monospace',
};

type FontPack = {
  "--pf-font-display": string;
  "--pf-font-body": string;
};

type TokenInput = {
  fonts?: FontPack;
  "--pf-bg-deep": string;
  "--pf-bg-panel": string;
  "--pf-bg-elevated": string;
  "--pf-purple": string;
  "--pf-purple-glow": string;
  "--pf-cyan": string;
  "--pf-cyan-glow": string;
  "--pf-magenta": string;
  "--pf-text": string;
  "--pf-text-muted": string;
  "--pf-border": string;
  "--pf-success"?: string;
  "--pf-danger"?: string;
  "--pf-accent-soft"?: string;
  "--pf-glow"?: string;
  "--pf-glow-secondary"?: string;
  "--pf-shadow"?: string;
  "--pf-scrollbar"?: string;
};

function tokens(partial: TokenInput): ThemeTokens {
  const fonts = partial.fonts ?? forgeFonts;
  const purple = partial["--pf-purple"];
  const cyan = partial["--pf-cyan"];
  return {
    ...fonts,
    "--pf-bg-deep": partial["--pf-bg-deep"],
    "--pf-bg-panel": partial["--pf-bg-panel"],
    "--pf-bg-elevated": partial["--pf-bg-elevated"],
    "--pf-purple": purple,
    "--pf-purple-glow": partial["--pf-purple-glow"],
    "--pf-cyan": cyan,
    "--pf-cyan-glow": partial["--pf-cyan-glow"],
    "--pf-magenta": partial["--pf-magenta"],
    "--pf-text": partial["--pf-text"],
    "--pf-text-muted": partial["--pf-text-muted"],
    "--pf-border": partial["--pf-border"],
    "--pf-success": partial["--pf-success"] ?? "#a3e635",
    "--pf-danger": partial["--pf-danger"] ?? "#f472b6",
    "--pf-accent-soft":
      partial["--pf-accent-soft"] ?? `color-mix(in srgb, ${purple} 22%, transparent)`,
    "--pf-glow": partial["--pf-glow"] ?? `color-mix(in srgb, ${purple} 45%, transparent)`,
    "--pf-glow-secondary":
      partial["--pf-glow-secondary"] ?? `color-mix(in srgb, ${cyan} 30%, transparent)`,
    "--pf-shadow":
      partial["--pf-shadow"] ??
      `0 18px 50px color-mix(in srgb, ${partial["--pf-bg-deep"]} 80%, black)`,
    "--pf-scrollbar":
      partial["--pf-scrollbar"] ?? `color-mix(in srgb, ${purple} 28%, transparent)`,
  };
}

export const THEMES: ThemeDefinition[] = [
  {
    id: "forge-neon",
    name: "Forge Neon",
    description: "Signature purple + cyan suite shell — default.",
    mode: "dark",
    preview: ["#0a0612", "#a78bfa", "#a3e635"],
    tokens: tokens({
      "--pf-bg-deep": "#0a0612",
      "--pf-bg-panel": "#120a1f",
      "--pf-bg-elevated": "#1a1030",
      "--pf-purple": "#a78bfa",
      "--pf-purple-glow": "#c084fc",
      "--pf-cyan": "#a3e635",
      "--pf-cyan-glow": "#67e8f9",
      "--pf-magenta": "#e879f9",
      "--pf-text": "#f3e8ff",
      "--pf-text-muted": "#a78bca",
      "--pf-border": "rgba(168, 85, 247, 0.25)",
      "--pf-glow": "rgba(168, 85, 247, 0.45)",
      "--pf-glow-secondary": "rgba(34, 211, 238, 0.3)",
    }),
  },
  {
    id: "ink-night",
    name: "Ink Night",
    description: "Luminous violet & gold — same family as VividLex default.",
    mode: "dark",
    preview: ["#0e0b18", "#a78bfa", "#f0d078"],
    tokens: tokens({
      fonts: cleanFonts,
      "--pf-bg-deep": "#0e0b18",
      "--pf-bg-panel": "#17122a",
      "--pf-bg-elevated": "#1f1836",
      "--pf-purple": "#a78bfa",
      "--pf-purple-glow": "#c4b5fd",
      "--pf-cyan": "#f0d078",
      "--pf-cyan-glow": "#fde68a",
      "--pf-magenta": "#c084fc",
      "--pf-text": "#f4f0ff",
      "--pf-text-muted": "#a89bc4",
      "--pf-border": "rgba(180, 160, 255, 0.16)",
      "--pf-success": "#4ade9b",
      "--pf-danger": "#fb7185",
      "--pf-glow": "rgba(167, 139, 250, 0.5)",
      "--pf-glow-secondary": "rgba(240, 208, 120, 0.28)",
    }),
  },
  {
    id: "aurora",
    name: "Aurora",
    description: "Cool teal night sky with northern-light accents.",
    mode: "dark",
    preview: ["#0a1218", "#2dd4bf", "#38bdf8"],
    tokens: tokens({
      fonts: cleanFonts,
      "--pf-bg-deep": "#0a1218",
      "--pf-bg-panel": "#0f1c24",
      "--pf-bg-elevated": "#152830",
      "--pf-purple": "#2dd4bf",
      "--pf-purple-glow": "#5eead4",
      "--pf-cyan": "#38bdf8",
      "--pf-cyan-glow": "#7dd3fc",
      "--pf-magenta": "#a3e635",
      "--pf-text": "#e8f4f8",
      "--pf-text-muted": "#7a9eab",
      "--pf-border": "rgba(125, 211, 252, 0.14)",
      "--pf-success": "#4ade80",
      "--pf-danger": "#fb7185",
      "--pf-glow": "rgba(45, 212, 191, 0.4)",
      "--pf-glow-secondary": "rgba(56, 189, 248, 0.3)",
    }),
  },
  {
    id: "parchment",
    name: "Parchment",
    description: "Warm paper & sepia ink — classic library shelf.",
    mode: "light",
    preview: ["#f4ebe0", "#9a4d1c", "#c4a35a"],
    tokens: tokens({
      fonts: serifFonts,
      "--pf-bg-deep": "#f4ebe0",
      "--pf-bg-panel": "#faf4ec",
      "--pf-bg-elevated": "#ebe0d0",
      "--pf-purple": "#9a4d1c",
      "--pf-purple-glow": "#c4a35a",
      "--pf-cyan": "#a67c2d",
      "--pf-cyan-glow": "#d4b46a",
      "--pf-magenta": "#8b4518",
      "--pf-text": "#2c2118",
      "--pf-text-muted": "#7a6554",
      "--pf-border": "rgba(90, 60, 30, 0.16)",
      "--pf-success": "#3d7a52",
      "--pf-danger": "#b33a3a",
      "--pf-accent-soft": "rgba(154, 77, 28, 0.12)",
      "--pf-glow": "rgba(196, 163, 90, 0.35)",
      "--pf-glow-secondary": "rgba(154, 77, 28, 0.2)",
      "--pf-shadow": "0 16px 40px rgba(80, 50, 20, 0.12)",
      "--pf-scrollbar": "rgba(90, 60, 30, 0.22)",
    }),
  },
  {
    id: "minimal-light",
    name: "Minimal Light",
    description: "Clean daylight UI — crisp, modern, high clarity.",
    mode: "light",
    preview: ["#f7f8fa", "#4f46e5", "#111827"],
    tokens: tokens({
      fonts: cleanFonts,
      "--pf-bg-deep": "#f7f8fa",
      "--pf-bg-panel": "#ffffff",
      "--pf-bg-elevated": "#eef0f4",
      "--pf-purple": "#4f46e5",
      "--pf-purple-glow": "#818cf8",
      "--pf-cyan": "#0ea5e9",
      "--pf-cyan-glow": "#38bdf8",
      "--pf-magenta": "#6366f1",
      "--pf-text": "#111827",
      "--pf-text-muted": "#6b7280",
      "--pf-border": "rgba(15, 23, 42, 0.1)",
      "--pf-success": "#059669",
      "--pf-danger": "#dc2626",
      "--pf-accent-soft": "rgba(79, 70, 229, 0.1)",
      "--pf-glow": "rgba(79, 70, 229, 0.28)",
      "--pf-glow-secondary": "rgba(14, 165, 233, 0.2)",
      "--pf-shadow": "0 12px 36px rgba(15, 23, 42, 0.08)",
      "--pf-scrollbar": "rgba(15, 23, 42, 0.12)",
    }),
  },
  {
    id: "plasma-ember",
    name: "Plasma Ember",
    description: "Forge heat — amber, orange, and hot metal.",
    mode: "dark",
    preview: ["#120806", "#f59e0b", "#ef4444"],
    tokens: tokens({
      "--pf-bg-deep": "#120806",
      "--pf-bg-panel": "#1c0f0a",
      "--pf-bg-elevated": "#2a1510",
      "--pf-purple": "#f59e0b",
      "--pf-purple-glow": "#fbbf24",
      "--pf-cyan": "#fb923c",
      "--pf-cyan-glow": "#fdba74",
      "--pf-magenta": "#ef4444",
      "--pf-text": "#fff7ed",
      "--pf-text-muted": "#c4a484",
      "--pf-border": "rgba(245, 158, 11, 0.28)",
      "--pf-success": "#4ade80",
      "--pf-danger": "#f87171",
      "--pf-glow": "rgba(245, 158, 11, 0.45)",
      "--pf-glow-secondary": "rgba(239, 68, 68, 0.3)",
    }),
  },
  {
    id: "void-matrix",
    name: "Void Matrix",
    description: "Terminal green on pure void — ops / hacker rack.",
    mode: "dark",
    preview: ["#030806", "#22c55e", "#4ade80"],
    tokens: tokens({
      fonts: monoFonts,
      "--pf-bg-deep": "#030806",
      "--pf-bg-panel": "#07120c",
      "--pf-bg-elevated": "#0c1a12",
      "--pf-purple": "#22c55e",
      "--pf-purple-glow": "#4ade80",
      "--pf-cyan": "#86efac",
      "--pf-cyan-glow": "#bbf7d0",
      "--pf-magenta": "#34d399",
      "--pf-text": "#d1fae5",
      "--pf-text-muted": "#6b9f80",
      "--pf-border": "rgba(34, 197, 94, 0.28)",
      "--pf-success": "#4ade80",
      "--pf-danger": "#f87171",
      "--pf-glow": "rgba(34, 197, 94, 0.4)",
      "--pf-glow-secondary": "rgba(74, 222, 128, 0.25)",
    }),
  },
  {
    id: "cold-steel",
    name: "Cold Steel",
    description: "Industrial blue-gray — datacenter / blade chassis.",
    mode: "dark",
    preview: ["#0b0f14", "#64748b", "#38bdf8"],
    tokens: tokens({
      fonts: cleanFonts,
      "--pf-bg-deep": "#0b0f14",
      "--pf-bg-panel": "#121820",
      "--pf-bg-elevated": "#1a222d",
      "--pf-purple": "#64748b",
      "--pf-purple-glow": "#94a3b8",
      "--pf-cyan": "#38bdf8",
      "--pf-cyan-glow": "#7dd3fc",
      "--pf-magenta": "#60a5fa",
      "--pf-text": "#e2e8f0",
      "--pf-text-muted": "#94a3b8",
      "--pf-border": "rgba(148, 163, 184, 0.2)",
      "--pf-success": "#a3e635",
      "--pf-danger": "#f87171",
      "--pf-glow": "rgba(56, 189, 248, 0.3)",
      "--pf-glow-secondary": "rgba(100, 116, 139, 0.35)",
    }),
  },

  {
    id: "rose-sakura-soft",
    name: "rose sakura soft pink cyber night",
    description: "rose sakura soft pink cyber night",
    mode: "dark",
    preview: ["#120810", "#f472b6", "#e879f9"],
    tokens: tokens({
      "--pf-bg-deep": "#120810",
      "--pf-bg-panel": "#1c0e18",
      "--pf-bg-elevated": "#2a1524",
      "--pf-purple": "#f472b6",
      "--pf-purple-glow": "#f687c1",
      "--pf-cyan": "#e879f9",
      "--pf-cyan-glow": "#eb89fa",
      "--pf-magenta": "#ef75d1",
      "--pf-text": "#fdf2f8",
      "--pf-text-muted": "#c4a0b4",
      "--pf-border": "color-mix(in srgb, #f472b6 28%, transparent)",
      "--pf-success": "#34d399",
      "--pf-danger": "#f472b6",
      "--pf-glow": "color-mix(in srgb, #f472b6 45%, transparent)",
      "--pf-glow-secondary": "color-mix(in srgb, #e879f9 30%, transparent)",
    }),
  },
];

/** AgentForge default — void-matrix pairs with lime ops accent #a3e635 */
export const DEFAULT_THEME_ID: ThemeId = "void-matrix";

export function getTheme(id: string | null | undefined): ThemeDefinition {
  return THEMES.find((t) => t.id === id) ?? THEMES[0];
}

export function isThemeId(id: string): id is ThemeId {
  return THEMES.some((t) => t.id === id);
}

export function listThemes(): ThemeDefinition[] {
  return THEMES;
}
